import { Badge } from "@/components/ui/Badge";

// Audit P2 portal-order-detail — payment-state chip in the order hero.
// Sits right of the Total chip so a customer reading the hero sees
// "$1,123 · Paid" at a glance instead of having to scroll to Invoices.
//
// Maps `orders.payment_state` (Order::PAYMENT_STATES in
// app/models/order.rb) to a labeled, semantically-toned badge.
// Unknown / nil payment_state intentionally renders nothing — the
// hero shouldn't show "Unknown" on an order that hasn't reached the
// payment step yet, and a bare machine name ("zombified") would leak
// internals.

type Tone = "default" | "success" | "warning" | "danger" | "muted";

interface Entry {
  label: string;
  tone: Tone;
}

const PAYMENT_MAP: Record<string, Entry> = {
  captured: { label: "Paid", tone: "success" },
  authorized: { label: "Auth held", tone: "warning" },
  capture_failed: { label: "Payment failed", tone: "danger" },
  refunded: { label: "Refunded", tone: "muted" },
  voided: { label: "Auth released", tone: "muted" }
};

export function PaymentChip({
  paymentState
}: {
  paymentState: string | null;
}): JSX.Element | null {
  if (!paymentState) return null;
  const entry = PAYMENT_MAP[paymentState];
  if (!entry) return null;
  return (
    <Badge
      role="status"
      variant={entry.tone}
      data-testid="payment-chip"
      data-payment-state={paymentState}
    >
      {entry.label}
    </Badge>
  );
}
